import { getItemLink } from '@/components/AppMenu/AppMenubar.utils';
import type { MenuNavigation, Permission } from '@/models/types';
import { userHasSomePermissions } from '@/shared/auth/useUserAffectations';
import DirectusIcon from '@/shared/icons/DirectusIcon';
import { Menu } from '@mantine/core';
import { IconChevronRight, IconExternalLink } from '@tabler/icons-react';
import isObject from 'lodash/isObject';
import { NavLink } from 'react-router-dom';

type Props = {
  item: MenuNavigation;
  userPermissions: Permission[];
};

const isVisibleMenuItem = (item: MenuNavigation, userPermissions: Permission[]): boolean => {
  if (item.status !== 'published') {
    return false;
  }

  const permission = isObject(item.permission) ? item.permission.id : item.permission;
  if (permission && !userHasSomePermissions(userPermissions, [permission])) {
    return false;
  }

  const children = Array.isArray(item.children) ? (item.children as MenuNavigation[]) : [];

  if (item.link) {
    return true;
  }

  return children.some(child => isVisibleMenuItem(child, userPermissions));
};

const AppMenubarChildItem = ({ item, userPermissions }: Props) => {
  const permission = isObject(item.permission) ? item.permission.id : item.permission;
  const hasPermission = !permission || userHasSomePermissions(userPermissions, [permission]);

  const children = Array.isArray(item.children) ? (item.children as MenuNavigation[]) : [];
  const visibleChildren = children.filter(child => isVisibleMenuItem(child, userPermissions));

  if (!hasPermission) {
    return null;
  }

  if (visibleChildren.length > 0) {
    return (
      <Menu.Sub>
        <Menu.Sub.Target>
          <Menu.Sub.Item
            leftSection={item.icon ? <DirectusIcon icon={item.icon} size={16} /> : undefined}
            rightSection={<IconChevronRight size={14} stroke={1.5} />}
          >
            {item.label ?? ''}
          </Menu.Sub.Item>
        </Menu.Sub.Target>
        <Menu.Sub.Dropdown>
          {visibleChildren.map(child => (
            <AppMenubarChildItem key={child.id} item={child} userPermissions={userPermissions} />
          ))}
        </Menu.Sub.Dropdown>
      </Menu.Sub>
    );
  }

  if (!item.link) {
    return null;
  }

  const link = getItemLink(item);

  return (
    <Menu.Item
      component={NavLink}
      to={link}
      target={item.linkType === 'external_link' ? '_blank' : undefined}
      rel={item.linkType === 'external_link' ? 'noopener noreferrer' : undefined}
      leftSection={item.icon ? <DirectusIcon icon={item.icon} size={16} /> : undefined}
      rightSection={
        item.linkType === 'external_link' ? <IconExternalLink size={14} stroke={1.5} /> : undefined
      }
    >
      {item.label ?? ''}
    </Menu.Item>
  );
};

export default AppMenubarChildItem;
